import React, { useState, useEffect } from 'react';
import apiService from '../../services/api';
import { useNavigate } from 'react-router-dom';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    nickname: '',
    phone: '',
    role: 'user'
  });
  const [editingUser, setEditingUser] = useState(null);
  const [formError, setFormError] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    // 获取当前登录用户信息
    const user = JSON.parse(localStorage.getItem('user'));
    if (!user || (user.role !== 'admin' && user.role !== 'union')) {
      navigate('/');
      return;
    }
    setCurrentUser(user);
    fetchUsers();
  }, [navigate]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      
      const response = await apiService.getUsers();
      
      setUsers(response.data);
    } catch (err) {
      setError('获取用户列表失败');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    
    try {
      const token = localStorage.getItem('token');
      
      if (editingUser) {
        // 更新用户
        await apiService.updateUser(editingUser.id, formData);
      } else {
        // 创建新用户
        await apiService.createUser(formData);
      }
      
      // 重置表单
      setFormData({
        username: '',
        password: '',
        nickname: '',
        phone: '',
        role: 'user'
      });
      setEditingUser(null);
      
      // 重新获取用户列表
      fetchUsers();
    } catch (err) {
      setFormError(err.response?.data?.message || '操作失败');
      console.error(err);
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      username: user.username,
      password: '', // 不显示密码
      nickname: user.nickname || '',
      phone: user.phone || '',
      role: user.role
    });
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('确定要删除此用户吗？')) {
      return;
    }
    
    try {
      await apiService.deleteUser(userId);
      
      // 重新获取用户列表
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.message || '删除用户失败');
      console.error(err);
    }
  };

  const cancelEdit = () => {
    setEditingUser(null);
    setFormData({
      username: '',
      password: '',
      nickname: '',
      phone: '',
      role: 'user'
    });
  };

  // 搜索过滤
  const filteredUsers = users.filter(u => {
    const kw = search.trim().toLowerCase();
    if (!kw) return true;
    return (
      (u.username && u.username.toLowerCase().includes(kw)) ||
      (u.nickname && u.nickname.toLowerCase().includes(kw)) ||
      (u.role && u.role.toLowerCase().includes(kw))
    );
  });

  if (loading && users.length === 0) {
    return (
      <div className="text-center my-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-2">加载中...</p>
      </div>
    );
  }

  return (
    <div>
      <h2 className="mb-4">用户管理</h2>
      {error && <div className="alert alert-danger">{error}</div>}
      
      <div className="row">
        <div className="col-md-8">
          <div className="card">
            <div className="card-header bg-primary text-white">
              <div className="d-flex justify-content-between align-items-center">
                <h4 className="mb-0">用户列表</h4>
                <input
                  type="text"
                  className="form-control form-control-sm ms-2"
                  style={{ maxWidth: 220 }}
                  placeholder="关键字搜索..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </div>
            </div>
            <div className="card-body">
              <div className="table-responsive">
                <table className="table table-hover">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>用户名</th>
                      <th style={{maxWidth: 120}}>昵称</th>
                      <th>角色</th>
                      <th>操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map(user => (
                      <tr key={user.id}>
                        <td>{user.id}</td>
                        <td>{user.username}</td>
                        <td style={{maxWidth: 120, wordBreak: 'break-all', whiteSpace: 'pre-wrap'}}>{user.nickname || '-'}</td>
                        <td>
                          {user.role === 'admin' ? (
                            <span className="badge bg-danger">管理员</span>
                          ) : user.role === 'union' ? (
                            <span className="badge bg-warning">工会委员</span>
                          ) : (
                            <span className="badge bg-info">普通用户</span>
                          )}
                        </td>
                        <td>
                          <button
                            className="btn btn-sm btn-outline-primary me-2"
                            onClick={() => handleEdit(user)}
                            disabled={currentUser.role === 'union' && user.role === 'admin'} // 工会委员不能编辑管理员
                          >
                            编辑
                          </button>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleDelete(user.id)}
                            disabled={user.username === 'admin' || (currentUser.role === 'union' && user.role === 'admin')} // 禁止删除admin用户或工会委员不能删除管理员
                          >
                            删除
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
        
        <div className="col-md-4">
          <div className="card">
            <div className="card-header bg-primary text-white">
              <h4 className="mb-0">{editingUser ? '编辑用户' : '添加用户'}</h4>
            </div>
            <div className="card-body">
              {formError && <div className="alert alert-danger">{formError}</div>}
              
              <form onSubmit={handleSubmit}>
                <div className="mb-3">
                  <label htmlFor="username" className="form-label">用户名</label>
                  <input
                    type="text"
                    className="form-control"
                    id="username"
                    name="username"
                    value={formData.username}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="mb-3">
                  <label htmlFor="password" className="form-label">
                    密码 {editingUser && <small className="text-muted">(留空表示不修改)</small>}
                  </label>
                  <input
                    type="password"
                    className="form-control"
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    required={!editingUser}
                    disabled={currentUser.role === 'union' && editingUser && editingUser.role === 'admin'} // 工会委员不能修改管理员密码
                  />
                </div>
                <div className="mb-3">
                  <label htmlFor="nickname" className="form-label">昵称</label>
                  <input
                    type="text"
                    className="form-control"
                    id="nickname"
                    name="nickname"
                    value={formData.nickname}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="mb-3">
                  <label htmlFor="phone" className="form-label">电话（可选）</label>
                  <input
                    type="text"
                    className="form-control"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="请输入电话号码"
                  />
                </div>
                <div className="mb-3">
                  <label htmlFor="role" className="form-label">角色</label>
                  <select
                    className="form-select"
                    id="role"
                    name="role"
                    value={formData.role}
                    onChange={handleInputChange}
                    disabled={currentUser.role === 'union' && editingUser && editingUser.role === 'admin'} // 工会委员不能修改管理员角色
                  >
                    <option value="user">普通用户</option>
                    {currentUser.role === 'admin' && <option value="admin">管理员</option>}
                    <option value="union">工会委员</option>
                  </select>
                </div>
                <div className="d-grid gap-2">
                  <button type="submit" className="btn btn-primary">
                    {editingUser ? '更新用户' : '添加用户'}
                  </button>
                  {editingUser && (
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={cancelEdit}
                    >
                      取消编辑
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Users;
